import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authApi, loginPageApi, getBackendUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Heart, Loader2, ArrowLeft, BookOpen, Award, FileText, User, HelpCircle, Mail, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import LegalDialog from '../common/LegalDialog';

// Fallback hero (only when no admin upload exists). Identical to LoginPage.jsx.
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1600&q=80';

export default function ParticipantAuthPage() {
    const location = useLocation();
    const isLoginRoute = location.pathname.includes('/login');
    const [activeTab, setActiveTab] = useState(isLoginRoute ? 'login' : 'register');
    const [searchParams] = useSearchParams();
    const redirectSeminar = searchParams.get('seminar');
    const navigate = useNavigate();
    const { login } = useAuth();

    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [regData, setRegData] = useState({
        salutation: '', academic_title: '', first_name: '', last_name: '', email: '', phone: '',
        birth_date: '', street: '', postal_code: '', city: '',
        password: '', password_confirm: '',
    });
    const [loading, setLoading] = useState(false);
    const [acceptLegal, setAcceptLegal] = useState(false);
    const [legalOpen, setLegalOpen] = useState(null); // 'terms_of_service' | 'privacy_policy' | null
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent, setForgotSent] = useState(false);
    const [registrationPending, setRegistrationPending] = useState(false);
    const [registrationMessage, setRegistrationMessage] = useState('');
    const [registeredEmail, setRegisteredEmail] = useState('');
    // UX (05/2026): expliziter Hinweis-Banner im Login-Tab, wenn das Konto
    // noch nicht bestätigt ist. Plus Resend-Button + lokaler 60s-Cooldown.
    const [loginUnverified, setLoginUnverified] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(false);

    // Honeypot-Felder – echte Nutzer sehen das Feld nicht (display:none + tabindex=-1),
    // automatisierte Spam-Bots dagegen füllen tendenziell ALLE Inputs aus.
    // Wenn der Wert nicht-leer ist → das Backend antwortet stillschweigend „OK", ohne
    // tatsächlich etwas anzulegen oder zu versenden.
    const [hpRegister, setHpRegister] = useState('');
    const [hpForgot, setHpForgot] = useState('');

    // Hero-Bild für das rechte Info-Panel (gleicher Endpoint wie Admin-Login).
    // Wenn der Admin im Settings-Bereich ein Bild hochlädt, folgt diese Seite
    // automatisch. Fällt zurück auf das Default-Unsplash-Bild.
    const [heroUrl, setHeroUrl] = useState(null);
    useEffect(() => {
        let mounted = true;
        loginPageApi.getPublic()
            .then((res) => { if (mounted) setHeroUrl(res.data?.hero_image_url || null); })
            .catch(() => { /* fallback below */ });
        return () => { mounted = false; };
    }, []);
    const heroSrc = heroUrl ? `${getBackendUrl()}${heroUrl}` : DEFAULT_HERO_IMAGE;

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoginUnverified(false);
        try {
            const user = await login(loginData.email, loginData.password);
            toast.success('Erfolgreich angemeldet');
            if (redirectSeminar) {
                navigate(`/seminare/${redirectSeminar}`);
            } else if (user.role === 'admin' || user.role === 'super_admin') {
                navigate('/admin');
            } else {
                navigate('/portal');
            }
        } catch (error) {
            const detail = error.response?.data?.detail || 'Anmeldung fehlgeschlagen';
            // Backend liefert bei nicht-bestätigtem Konto 403 mit Text „… nicht bestätigt …".
            // Statt eines generischen Toasts blenden wir einen Resend-Banner ein.
            const isUnverified = error.response?.status === 403
                && typeof detail === 'string'
                && detail.toLowerCase().includes('bestät');
            if (isUnverified) {
                setLoginUnverified(true);
            } else {
                toast.error(detail);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async (email) => {
        const target = (email || loginData.email || registeredEmail || '').trim();
        if (!target || resending || resendCooldown) return;
        setResending(true);
        try {
            const res = await authApi.resendVerification(target);
            const status = res.data?.status;
            if (status === 'cooldown') {
                toast.info('Bitte kurz warten, bevor Sie eine erneute Mail anfordern.');
            } else if (status === 'rate_limited') {
                toast.error('Zu viele Anfragen. Bitte in einer Stunde erneut versuchen.');
            } else {
                toast.success('Bestätigungs-E-Mail erneut versendet. Bitte Postfach prüfen.');
            }
            setResendCooldown(true);
            setTimeout(() => setResendCooldown(false), 60000);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erneutes Senden fehlgeschlagen');
        } finally {
            setResending(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authApi.forgotPassword(forgotEmail, hpForgot);
            setForgotSent(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Senden');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!acceptLegal) {
            toast.error('Bitte akzeptieren Sie die Nutzungsbedingungen und Datenschutzhinweise');
            return;
        }
        if (regData.password !== regData.password_confirm) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        if (regData.password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        if (!regData.birth_date || !regData.street || !regData.postal_code || !regData.city) {
            toast.error('Bitte füllen Sie Geburtsdatum, Straße, PLZ und Ort aus');
            return;
        }
        setLoading(true);
        try {
            const response = await authApi.registerParticipant({
                salutation: regData.salutation,
                academic_title: regData.academic_title,
                first_name: regData.first_name,
                last_name: regData.last_name,
                email: regData.email,
                phone: regData.phone,
                birth_date: regData.birth_date,
                street: regData.street,
                postal_code: regData.postal_code,
                city: regData.city,
                password: regData.password,
                hp_website: hpRegister,
            });
            setRegistrationMessage(response.data.message);
            setRegisteredEmail(regData.email);
            setRegistrationPending(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        { icon: BookOpen, text: 'Seminar-Buchungen verwalten' },
        { icon: Award, text: 'Fortbildungsnachweise einsehen' },
        { icon: FileText, text: 'Zertifikate & Lizenzen herunterladen' },
        { icon: User, text: 'Persönliche Daten pflegen' },
    ];

    return (
        <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] bg-white">
            {/* LEFT — auth form column */}
            <div className="flex flex-col px-6 py-8 sm:px-12 lg:px-16 xl:px-24">
                {/* Top strip: back link + small brand (mobile only) */}
                <div className="flex items-center justify-between mb-8">
                    <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm py-3 min-h-[44px]" data-testid="back-to-seminars">
                        <ArrowLeft className="h-4 w-4" />
                        Zurück zu den Seminaren
                    </Link>
                    <div className="flex items-center gap-2 lg:hidden">
                        <Heart className="h-5 w-5 text-primary" fill="currentColor" />
                        <span className="text-sm font-semibold text-gray-700">BGPR-Akademie</span>
                    </div>
                </div>

                {/* Brand wordmark (desktop only) */}
                <div className="hidden lg:flex items-center gap-3 mb-10" data-testid="auth-brand">
                    <div className="p-2.5 rounded-xl bg-primary shadow-sm">
                        <Heart className="h-6 w-6 text-white" fill="currentColor" />
                    </div>
                    <div className="leading-tight">
                        <div className="text-2xl font-bold tracking-tight text-gray-900">
                            BGPR<span className="text-primary">-Akademie</span>
                        </div>
                        <div className="text-[11px] tracking-[0.18em] uppercase text-gray-500 mt-0.5">
                            Fort- &amp; Weiterbildungen
                        </div>
                    </div>
                </div>

                {/* Form area */}
                <div className="w-full max-w-md mx-auto lg:mx-0 flex-1 flex flex-col justify-center">
                    <div className="bg-white">
                        {/* Tabs */}
                            <div className="flex border-b border-gray-100" data-testid="auth-tabs">
                                <button
                                    onClick={() => setActiveTab('register')}
                                    className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                                        activeTab === 'register'
                                            ? 'text-primary'
                                            : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                    data-testid="tab-register"
                                >
                                    Konto erstellen
                                    {activeTab === 'register' && (
                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                                    )}
                                </button>
                                <button
                                    onClick={() => setActiveTab('login')}
                                    className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                                        activeTab === 'login'
                                            ? 'text-primary'
                                            : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                    data-testid="tab-login"
                                >
                                    Bereits registriert? Anmelden
                                    {activeTab === 'login' && (
                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                                    )}
                                </button>
                            </div>

                            <div className="p-6 sm:p-8">
                                {activeTab === 'register' ? (
                                    registrationPending ? (
                                        <div className="py-2" data-testid="registration-success-card">
                                            <div className="text-center">
                                                <div className="mx-auto mb-4 p-4 bg-green-100 rounded-full w-fit">
                                                    <Mail className="h-12 w-12 text-green-600" />
                                                </div>
                                                <h2 className="text-xl font-bold text-gray-900 mb-2">E-Mail-Bestätigung erforderlich</h2>
                                                <p className="text-sm text-gray-600 leading-relaxed mb-1">
                                                    Wir haben Ihnen eine Bestätigungs-E-Mail an
                                                </p>
                                                <p className="font-semibold text-gray-900 mb-3">{registeredEmail}</p>
                                                <p className="text-sm text-gray-600 leading-relaxed">gesendet.</p>
                                            </div>

                                            <div className="my-5 bg-amber-50 border-l-4 border-amber-500 p-3.5 rounded-r-md">
                                                <div className="flex items-start gap-2.5">
                                                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div className="text-sm text-amber-900">
                                                        <strong>Wichtig:</strong> Sie können sich <strong>noch nicht</strong> einloggen
                                                        oder Seminare buchen, bis Sie auf den Link in der E-Mail geklickt haben.
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-2.5 mb-5">
                                                <div className="font-semibold text-gray-900 text-sm">So geht es weiter:</div>
                                                <ol className="space-y-2 text-sm text-gray-700">
                                                    <li className="flex gap-3">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                                                        <span>Öffnen Sie Ihr Postfach und prüfen Sie auch den <strong>Spam-Ordner</strong>.</span>
                                                    </li>
                                                    <li className="flex gap-3">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                                                        <span>Klicken Sie auf den Bestätigungs-Link in der E-Mail (gültig 24 Stunden).</span>
                                                    </li>
                                                    <li className="flex gap-3">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                                                        <span>Anschließend können Sie sich einloggen und Seminare buchen.</span>
                                                    </li>
                                                </ol>
                                            </div>

                                            <div className="border-t pt-4 space-y-3">
                                                <div className="text-xs text-muted-foreground text-center">
                                                    Keine E-Mail erhalten? Prüfen Sie zuerst den Spam-Ordner.
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    className="w-full"
                                                    onClick={() => handleResendVerification(registeredEmail)}
                                                    disabled={resending || resendCooldown}
                                                    data-testid="resend-verification-btn"
                                                >
                                                    {resending ? (
                                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gesendet...</>
                                                    ) : resendCooldown ? (
                                                        'Bitte 60 Sekunden warten…'
                                                    ) : (
                                                        <><Mail className="mr-2 h-4 w-4" /> Bestätigungs-E-Mail erneut senden</>
                                                    )}
                                                </Button>
                                                <div className="flex flex-col sm:flex-row gap-2 justify-between text-xs">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setActiveTab('login'); }}
                                                        className="text-primary hover:underline font-medium text-left"
                                                        data-testid="goto-login-after-register"
                                                    >
                                                        Bereits bestätigt? Zur Anmeldung →
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setRegistrationPending(false); setRegisteredEmail(''); }}
                                                        className="text-muted-foreground hover:text-primary text-left sm:text-right"
                                                        data-testid="edit-email-link"
                                                    >
                                                        Falsche E-Mail-Adresse?
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                    <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                                        {/* Honeypot – unsichtbares Feld nur für Spam-Bots.
                                            Wird per CSS aus dem Layout entfernt, ist nicht fokussierbar (tabindex=-1),
                                            autocomplete=off + aria-hidden, damit Screenreader es ignorieren. */}
                                        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                                            <label htmlFor="reg_hp_website">Website (bitte leer lassen)</label>
                                            <input
                                                type="text"
                                                id="reg_hp_website"
                                                name="website"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={hpRegister}
                                                onChange={e => setHpRegister(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-900 mb-1">Neues Konto anlegen</h2>
                                            <p className="text-sm text-gray-500 mb-3">
                                                Erstellen Sie Ihren persönlichen Zugang zur BGPR-Akademie.
                                            </p>
                                            <div className="mb-5 bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900 flex items-start gap-2.5" data-testid="register-info-banner">
                                                <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    Nach dem Klick auf <strong>Konto erstellen</strong> senden wir Ihnen eine
                                                    Bestätigungs-E-Mail. Erst nach Klick auf den Link in der Mail können Sie
                                                    sich anmelden und Seminare buchen.
                                                </div>
                                            </div>
                                        </div>
                                        {/* Anrede + Titel */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_salutation">Anrede</Label>
                                                <select
                                                    id="reg_salutation"
                                                    value={regData.salutation}
                                                    onChange={(e) => setRegData({ ...regData, salutation: e.target.value })}
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                    data-testid="reg-salutation"
                                                >
                                                    <option value="">Bitte wählen</option>
                                                    <option value="Frau">Frau</option>
                                                    <option value="Herr">Herr</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_title">Titel <span className="font-normal text-gray-400">(optional)</span></Label>
                                                <Input
                                                    id="reg_title"
                                                    placeholder="z.B. Dr., Prof."
                                                    value={regData.academic_title}
                                                    onChange={(e) => setRegData({ ...regData, academic_title: e.target.value })}
                                                    data-testid="reg-title"
                                                />
                                            </div>
                                        </div>
                                        {/* Name */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_first_name">Vorname *</Label>
                                                <Input
                                                    id="reg_first_name"
                                                    placeholder="Max"
                                                    value={regData.first_name}
                                                    onChange={(e) => setRegData({ ...regData, first_name: e.target.value })}
                                                    required
                                                    data-testid="reg-firstname"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_last_name">Nachname *</Label>
                                                <Input
                                                    id="reg_last_name"
                                                    placeholder="Mustermann"
                                                    value={regData.last_name}
                                                    onChange={(e) => setRegData({ ...regData, last_name: e.target.value })}
                                                    required
                                                    data-testid="reg-lastname"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg_email">E-Mail *</Label>
                                            <Input
                                                id="reg_email"
                                                type="email"
                                                placeholder="ihre@email.de"
                                                value={regData.email}
                                                onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                                                required
                                                data-testid="reg-email"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg_phone">Telefon</Label>
                                            <Input
                                                id="reg_phone"
                                                type="tel"
                                                placeholder="0170 1234567"
                                                value={regData.phone}
                                                onChange={(e) => setRegData({ ...regData, phone: e.target.value })}
                                                data-testid="reg-phone"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg_birth_date">Geburtsdatum *</Label>
                                            <Input
                                                id="reg_birth_date"
                                                type="date"
                                                value={regData.birth_date}
                                                onChange={(e) => setRegData({ ...regData, birth_date: e.target.value })}
                                                required
                                                max={new Date().toISOString().split('T')[0]}
                                                data-testid="reg-birth-date"
                                            />
                                            <p className="text-xs text-gray-500">Wird für die Ausstellung Ihrer Lizenz benötigt.</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg_street">Straße und Hausnummer *</Label>
                                            <Input
                                                id="reg_street"
                                                placeholder="Musterstraße 1"
                                                value={regData.street}
                                                onChange={(e) => setRegData({ ...regData, street: e.target.value })}
                                                required
                                                data-testid="reg-street"
                                            />
                                        </div>
                                        <div className="grid grid-cols-[120px_1fr] gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_postal_code">PLZ *</Label>
                                                <Input
                                                    id="reg_postal_code"
                                                    placeholder="10115"
                                                    value={regData.postal_code}
                                                    onChange={(e) => setRegData({ ...regData, postal_code: e.target.value })}
                                                    required
                                                    inputMode="numeric"
                                                    pattern="[0-9]{4,5}"
                                                    data-testid="reg-postal-code"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_city">Ort *</Label>
                                                <Input
                                                    id="reg_city"
                                                    placeholder="Berlin"
                                                    value={regData.city}
                                                    onChange={(e) => setRegData({ ...regData, city: e.target.value })}
                                                    required
                                                    data-testid="reg-city"
                                                />
                                            </div>
                                        </div>
                                        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-900 leading-snug">
                                            Adresse und Geburtsdatum benötigen wir, um Ihre Lizenz auszustellen und Rechnungen für gebuchte Fortbildungen zu erstellen.
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_password">Passwort * <span className="font-normal text-gray-400">(min. 6 Zeichen)</span></Label>
                                                <Input
                                                    id="reg_password"
                                                    type="password"
                                                    placeholder="Passwort"
                                                    value={regData.password}
                                                    onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                                                    required
                                                    minLength={6}
                                                    data-testid="reg-password"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="reg_password_confirm">Passwort wiederholen *</Label>
                                                <Input
                                                    id="reg_password_confirm"
                                                    type="password"
                                                    placeholder="Wiederholen"
                                                    value={regData.password_confirm}
                                                    onChange={(e) => setRegData({ ...regData, password_confirm: e.target.value })}
                                                    required
                                                    minLength={6}
                                                    data-testid="reg-password-confirm"
                                                />
                                            </div>
                                        </div>
                                        {/* Checkbox AGB + Datenschutz */}
                                        <div className="pt-2">
                                            <label className="flex items-start gap-3 cursor-pointer py-1" data-testid="legal-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={acceptLegal}
                                                    onChange={(e) => setAcceptLegal(e.target.checked)}
                                                    className="mt-0.5 h-5 w-5 rounded border-gray-300 accent-primary shrink-0"
                                                    data-testid="reg-accept-legal"
                                                />
                                                <span className="text-sm text-gray-600">
                                                    Ich akzeptiere die{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => setLegalOpen('terms_of_service')}
                                                        className="text-primary hover:underline font-medium"
                                                        data-testid="open-terms-dialog"
                                                    >
                                                        Nutzungsbedingungen
                                                    </button>{' '}
                                                    und habe die{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => setLegalOpen('privacy_policy')}
                                                        className="text-primary hover:underline font-medium"
                                                        data-testid="open-privacy-dialog"
                                                    >
                                                        Datenschutzhinweise
                                                    </button>{' '}
                                                    gelesen. *
                                                </span>
                                            </label>
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full bg-primary hover:bg-primary/80 h-11 text-base"
                                            disabled={loading || !acceptLegal}
                                            data-testid="reg-submit"
                                        >
                                            {loading ? (
                                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt...</>
                                            ) : (
                                                'Konto erstellen'
                                            )}
                                        </Button>
                                    </form>
                                    )
                                ) : forgotMode ? (
                                    forgotSent ? (
                                        <div className="py-4 text-center" data-testid="forgot-success">
                                            <div className="mx-auto mb-4 p-3 bg-green-100 rounded-xl w-fit">
                                                <Heart className="h-8 w-8 text-green-600" />
                                            </div>
                                            <h2 className="text-lg font-bold text-gray-900 mb-2">E-Mail gesendet</h2>
                                            <p className="text-sm text-gray-500 mb-6">
                                                Falls ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts.
                                            </p>
                                            <button
                                                onClick={() => { setForgotMode(false); setForgotSent(false); }}
                                                className="text-primary hover:underline font-medium text-sm"
                                                data-testid="back-to-login"
                                            >
                                                Zurück zum Login
                                            </button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
                                            {/* Honeypot – unsichtbares Feld nur für Spam-Bots. */}
                                            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                                                <label htmlFor="forgot_hp_website">Website (bitte leer lassen)</label>
                                                <input
                                                    type="text"
                                                    id="forgot_hp_website"
                                                    name="website"
                                                    tabIndex={-1}
                                                    autoComplete="off"
                                                    value={hpForgot}
                                                    onChange={e => setHpForgot(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-900 mb-1">Passwort vergessen?</h2>
                                                <p className="text-sm text-gray-500 mb-5">
                                                    Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="forgot_email">E-Mail</Label>
                                                <Input
                                                    id="forgot_email"
                                                    type="email"
                                                    placeholder="ihre@email.de"
                                                    value={forgotEmail}
                                                    onChange={(e) => setForgotEmail(e.target.value)}
                                                    required
                                                    data-testid="forgot-email"
                                                />
                                            </div>
                                            <Button
                                                type="submit"
                                                className="w-full bg-primary hover:bg-primary/80 h-11 text-base"
                                                disabled={loading}
                                                data-testid="forgot-submit"
                                            >
                                                {loading ? (
                                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gesendet...</>
                                                ) : (
                                                    'Link anfordern'
                                                )}
                                            </Button>
                                            <div className="text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setForgotMode(false)}
                                                    className="text-sm text-gray-500 hover:text-gray-700"
                                                    data-testid="cancel-forgot"
                                                >
                                                    Zurück zum Login
                                                </button>
                                            </div>
                                        </form>
                                    )
                                ) : (
                                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                                        {loginUnverified && (
                                            <div
                                                className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-3"
                                                data-testid="unverified-email-banner"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div className="text-sm text-amber-900">
                                                        <strong>Ihre E-Mail-Adresse wurde noch nicht bestätigt.</strong>
                                                        <p className="mt-1">
                                                            Bitte prüfen Sie Ihr Postfach (auch den Spam-Ordner) und
                                                            klicken Sie auf den Bestätigungs-Link in der E-Mail.
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full bg-white"
                                                    onClick={() => handleResendVerification(loginData.email)}
                                                    disabled={resending || resendCooldown}
                                                    data-testid="login-resend-verification-btn"
                                                >
                                                    {resending ? (
                                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gesendet...</>
                                                    ) : resendCooldown ? (
                                                        'Bitte 60 Sekunden warten…'
                                                    ) : (
                                                        <><Mail className="mr-2 h-4 w-4" /> Bestätigungs-E-Mail erneut senden</>
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-900 mb-1">Willkommen zurück</h2>
                                            <p className="text-sm text-gray-500 mb-5">
                                                Melden Sie sich mit Ihren bestehenden Zugangsdaten an.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login_email">E-Mail</Label>
                                            <Input
                                                id="login_email"
                                                type="email"
                                                placeholder="ihre@email.de"
                                                value={loginData.email}
                                                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                                required
                                                data-testid="login-email"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="login_password">Passwort</Label>
                                                <button
                                                    type="button"
                                                    onClick={() => setForgotMode(true)}
                                                    className="text-xs text-primary hover:underline font-medium py-2 px-1 min-h-[44px] inline-flex items-center"
                                                    data-testid="forgot-password-link"
                                                >
                                                    Passwort vergessen?
                                                </button>
                                            </div>
                                            <Input
                                                id="login_password"
                                                type="password"
                                                placeholder="Ihr Passwort"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                                required
                                                data-testid="login-password"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full bg-primary hover:bg-primary/80 h-11 text-base"
                                            disabled={loading}
                                            data-testid="login-submit"
                                        >
                                            {loading ? (
                                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird angemeldet...</>
                                            ) : (
                                                'Anmelden'
                                            )}
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="text-center text-xs text-gray-400 mt-8" data-testid="auth-footer">
                        © {new Date().getFullYear()} Berliner Gesellschaft für Prävention und Rehabilitation
                        von Herzkreislauferkrankungen e.&nbsp;V.
                    </div>
                </div>

                {/* RIGHT — info panel (BGPR-Hero-Bild + Benefits Overlay) */}
                <div
                    className="hidden lg:flex relative overflow-hidden flex-col justify-center text-white"
                    style={{
                        backgroundImage: `url("${heroSrc}")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'right center',
                    }}
                    data-testid="auth-info-panel"
                >
                    {/* Asymmetric dark scrim — darker on the LEFT where the
                        benefits + headline sit, almost transparent on the
                        RIGHT so the photo motif (heart + stethoscope on the
                        BGPR login image) stays visible. Same approach as the
                        admin login hero. */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'linear-gradient(90deg, rgba(20,40,50,0.85) 0%, rgba(31,67,80,0.75) 45%, rgba(0,0,0,0.30) 75%, rgba(0,0,0,0.10) 100%)',
                        }}
                        data-testid="auth-info-tint"
                    />
                    {/* Soft radial glow on the lower-left to deepen contrast
                        under the benefits stack. */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'radial-gradient(ellipse 55% 65% at 25% 55%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)',
                        }}
                        data-testid="auth-info-overlay"
                    />
                    {/* Subtle teal accent light (top-left) — keeps the BGPR-CI feel. */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'radial-gradient(circle at 15% 20%, rgba(0,146,181,0.30), transparent 50%)',
                        }}
                    />
                    <div
                        className="relative px-12 xl:px-20 py-16 max-w-xl"
                        style={{ textShadow: '0 2px 12px rgba(0,0,0,0.55)' }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-md rounded-full text-xs font-semibold mb-6 ring-1 ring-white/25">
                            <Heart className="h-3.5 w-3.5" fill="currentColor" />
                            BGPR-Akademie
                        </div>
                        <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight mb-3">
                            Ihr persönlicher Zugang
                        </h2>
                        <div className="h-[3px] w-20 rounded-full mb-6" style={{ backgroundColor: 'hsl(var(--primary))' }} />
                        <p className="text-base text-white/95 leading-relaxed mb-8">
                            Mit Ihrem BGPR-Akademie-Konto haben Sie jederzeit Zugriff
                            auf Ihre Buchungen, Fortbildungsnachweise und Zertifikate.
                        </p>
                        <div className="space-y-3 mb-8">
                            {benefits.map((b, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-3 p-3 bg-black/25 backdrop-blur-md rounded-xl ring-1 ring-white/20"
                                    data-testid={`auth-benefit-${i}`}
                                >
                                    <div className="p-2 bg-white/20 rounded-lg shrink-0">
                                        <b.icon className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-white">{b.text}</span>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/hilfe?from=register"
                            className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white hover:underline font-medium"
                            data-testid="auth-faq-link"
                        >
                            <HelpCircle className="h-4 w-4" />
                            Warum brauche ich ein Konto? Häufige Fragen
                        </Link>
                    </div>
                </div>
            <LegalDialog
                type={legalOpen}
                open={!!legalOpen}
                onOpenChange={(o) => { if (!o) setLegalOpen(null); }}
            />
        </div>
    );
}
